import { useState, useRef, useCallback, useEffect } from "react";
import { addCategoiresForm } from "../../lib/actions";
import { useNavigate } from "react-router-dom";
import {
  FiUploadCloud,
  FiLink,
  FiMinimize2,
  FiMaximize2,
  FiSettings,
} from "react-icons/fi";
import {
  MdOutlineDeleteOutline,
  MdAddCircleOutline,
  MdLayers,
} from "react-icons/md";
import DropDownFilterPaginations from "../../components/GDropDownList/DropDownFilterPaginations";
import { ThreeDot } from "react-loading-indicators";
import { useTranslation } from "react-i18next";
import { BiSortAlt2 } from "react-icons/bi";

const InputConfigurator = ({
  input,
  config,
  updateConfig,
  currentLang,
  allConfigs,
  inputsData,
  generateNewId,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [optionSearch, setOptionSearch] = useState("");
  useEffect(() => {
    if (!isFullscreen) {
      setOptionSearch("");
      setParentOptionFilter("");
    }
  }, [isFullscreen]);
  const [parentOptionFilter, setParentOptionFilter] = useState("");
  const handleFilteringToggle = () => {
    const newValue = config.is_filtering === 1 ? 0 : 1;
    updateConfig(input.id, "is_filtering", newValue);
  };

  const handleTypeFilteringChange = (e) => {
    updateConfig(input.id, "type_filtering", e.target.value);
  };

  const handlePriorityChange = (e) => {
    updateConfig(input.id, "priority", e.target.value);
  };

  const handleParentChange = (e) => {
    updateConfig(input.id, "parent_id", e.target.value);
  };

  const availableParents = allConfigs.filter((c) => c.id !== input.id);

  const selectedParentConfig = config.parent_id
    ? allConfigs.find((c) => c.id.toString() === config.parent_id.toString())
    : null;

  const getInputLabel = (id) => {
    const inp = inputsData.find((i) => i.id === id);
    return inp ? inp.label?.[currentLang] || inp.label?.en : "Unknown";
  };

  const getFilterTypeOptions = () => {
    switch (input.type) {
      case "number":
      case "integer":
      case "float":
        return [
          {
            value: "value",
            label:
              currentLang === "en"
                ? "Value (Exact Match)"
                : "قيمة (مطابقة تامة)",
          },
          {
            value: "range",
            label:
              currentLang === "en" ? "Range (Min/Max)" : "نطاق (الأدنى/الأعلى)",
          },
        ];
      case "select":
      case "multi_select":
      case "radio":
      case "checkbox":
        return [
          {
            value: "select",
            label: currentLang === "en" ? "Choose (Single)" : "اختيار (واحد)",
          },
          {
            value: "multi_select",
            label:
              currentLang === "en" ? "Choose (Multiple)" : "اختيار (متعدد)",
          },
        ];
      case "text":
      case "textarea":
        return [
          {
            value: "text_search",
            label: currentLang === "en" ? "Text Search" : "بحث نصي",
          },
        ];
      default:
        return [];
    }
  };

  const addOption = () => {
    // 1. Generate Positive ID (1, 2, 3...)
    const tempId = generateNewId();

    const newOptions = [
      ...(config.options || []),
      {
        id: tempId,
        en: "",
        ar: "",
        priority: 0,
        relation_id: "",
      },
    ];
    updateConfig(input.id, "options", newOptions);
  };

  const updateOption = (index, key, value) => {
    const newOptions = [...(config.options || [])];
    if (!newOptions[index]) return;
    newOptions[index] = { ...newOptions[index], [key]: value };
    updateConfig(input.id, "options", newOptions);
  };

  const removeOption = (index) => {
    const newOptions = (config.options || []).filter((_, i) => i !== index);
    updateConfig(input.id, "options", newOptions);
  };

  const isSelectType = ["select", "multi_select", "radio", "checkbox"].includes(
    input.type,
  );
  const filterOptions = getFilterTypeOptions();
  const filteredOptions = (config.options || []).filter((option) => {
    const search = optionSearch.toLowerCase();

    const matchesText =
      !optionSearch ||
      option.en?.toLowerCase().includes(search) ||
      option.ar?.toLowerCase().includes(search);

    const matchesParent =
      !parentOptionFilter ||
      option.relation_id?.toString() === parentOptionFilter;

    return matchesText && matchesParent;
  });
const containerClasses = isFullscreen
    ? "fixed inset-0 z-50 bg-black/80 backdrop-blur-md overflow-y-auto p-4 sm:p-6 flex justify-center items-center"
    : "p-5 bg-black/20 rounded-xl mt-4 border border-gray-700/60 relative transition-all duration-300 shadow-inner";

  const innerContentClasses = isFullscreen
    ? "w-full max-w-7xl bg-gray-900 p-6 sm:p-8 rounded-2xl shadow-2xl border border-gray-700 flex flex-col max-h-[90vh] overflow-y-auto custom-scrollbar"
    : "space-y-6";

  return (
    <div className={containerClasses}>
      <div className={innerContentClasses}>
        <div
          className={`flex justify-between items-center mb-6 ${!isFullscreen ? "absolute top-4 right-4 rtl:left-4 rtl:right-auto" : ""}`}
        >
          {isFullscreen && (
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <FiSettings className="text-secondary" />
              {currentLang === "en"
                ? "Advanced Configuration"
                : "إعدادات متقدمة"}
            </h3>
          )}
          <button
            type="button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="text-gray-400 hover:text-white bg-gray-800/50 hover:bg-gray-700/80 p-2 rounded-lg transition-all flex items-center gap-2"
            title={
              isFullscreen
                ? currentLang === "en"
                  ? "Close"
                  : "إغلاق"
                : currentLang === "en"
                  ? "Expand"
                  : "توسيع"
            }
          >
            {isFullscreen ? (
              <FiMinimize2 size={18} />
            ) : (
              <FiMaximize2 size={18} />
            )}
            {isFullscreen && (
              <span className="text-sm font-medium">
                {currentLang === "en" ? "Close" : "إغلاق"}
              </span>
            )}
          </button>
        </div>

        {/* Input Priority & Parent */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="text-xs text-gray-400 block mb-1">
              {currentLang === "en" ? "Input Priority" : "أولوية الحقل"}
            </label>
            <div className="flex items-center bg-thirdly/50 rounded-lg px-2 border border-gray-600">
              <BiSortAlt2 className="text-secondary" />
              <input
                type="number"
                value={config.priority || 0}
                onChange={handlePriorityChange}
                className="w-full bg-transparent border-none text-white focus:ring-0 p-2"
                placeholder="0"
              />
            </div>
          </div>

          <div className="flex-1">
            <label className="text-xs text-gray-400 block mb-1">
              {currentLang === "en"
                ? "Parent Input (Optional)"
                : "الحقل الأصلي (اختياري)"}
            </label>
            <div className="flex items-center bg-thirdly/50 rounded-lg px-2 border border-gray-600">
              <FiLink className="text-blue-400" />
              <select
                value={config.parent_id || ""}
                onChange={handleParentChange}
                className="w-full  bg-thirdly/50 border-none text-white  focus:ring-0 p-1"
              >
                <option value="">
                  {currentLang === "en" ? "No Parent" : "بدون أصل"}
                </option>
                {availableParents.map((parent) => (
                  <option key={parent.id} value={parent.id}>
                    {getInputLabel(parent.id)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <hr className="border-gray-700" />

        {/* Filtering */}
        <h4 className="text-white mt-3 font-semibold text-sm">
          {currentLang === "en" ? "Filtering Configuration" : "إعدادات التصفية"}
        </h4>

        {filterOptions.length > 0 ? (
          <div className="flex items-center justify-between p-3 bg-thirdly/50 rounded-lg">
            <label className="text-white text-sm">
              {currentLang === "en" ? "Enable Filtering" : "تفعيل التصفية"}
            </label>
            <button
              type="button"
              onClick={handleFilteringToggle}
              className={`relative w-10 h-5 rounded-full transition-colors ${config.is_filtering === 1 ? "bg-green-600" : "bg-gray-500"}`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-200 ${config.is_filtering === 1 ? (currentLang === "ar" ? "left-0.5" : "left-5.5") : currentLang === "ar" ? "left-5.5" : "left-0.5"}`}
              />
            </button>
          </div>
        ) : (
          <p className="text-gray-400 text-sm">
            {currentLang === "en"
              ? "Filtering not applicable."
              : "التصفية غير متاحة."}
          </p>
        )}

        {config.is_filtering === 1 && filterOptions.length > 0 && (
          <div className="space-y-4 p-3  bg-thirdly/50 rounded-lg">
            <label className="block text-white text-sm">
              {currentLang === "en"
                ? "Filter Interface Type"
                : "واجهة نوع التصفية"}
            </label>
            <select
              value={config.type_filtering || ""}
              onChange={handleTypeFilteringChange}
              className="w-full px-2 py-1 bg-furthly border border-gray-500/100 text-white rounded-lg text-sm"
            >
              <option value="">
                {currentLang === "en" ? "Select Type" : "اختر النوع"}
              </option>
              {filterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {isSelectType && (
          <>
            {isFullscreen && (
              <div className="grid grid-cols-1 mt-3 md:grid-cols-2 gap-3 mb-4">
                <input
                  type="text"
                  value={optionSearch}
                  onChange={(e) => setOptionSearch(e.target.value)}
                  placeholder={
                    currentLang === "en"
                      ? "Search options..."
                      : "بحث في الخيارات..."
                  }
                  className="px-3 py-2 bg-thirdly border border-gray-600 rounded-lg text-white"
                />

                {config.parent_id &&
                  selectedParentConfig?.options?.length > 0 && (
                    <select
                      value={parentOptionFilter}
                      onChange={(e) => setParentOptionFilter(e.target.value)}
                      className="px-3 py-2 bg-thirdly border border-gray-600 rounded-lg text-white"
                    >
                      <option value="">
                        {currentLang === "en"
                          ? "All Parent Options"
                          : "كل خيارات الأب"}
                      </option>

                      {selectedParentConfig.options.map((parentOpt) => (
                        <option key={parentOpt.id} value={parentOpt.id}>
                          {currentLang === "en" ? parentOpt.en : parentOpt.ar}
                        </option>
                      ))}
                    </select>
                  )}
              </div>
            )}

            <div className="space-y-3 pt-5 border-t border-gray-600">
              <div className="flex justify-between items-center">
              <label className="text-white font-semibold flex items-center gap-2">
                {currentLang === "en" ? "Options" : "الخيارات"}
                <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">
                  {filteredOptions.length}
                </span>
              </label>
                
                <button
                  type="button"
                  onClick={addOption}
                  className="text-secondary text-sm hover:text-white transition flex items-center gap-1 bg-thirdly px-3 py-1 rounded-lg hover:bg-secondary/80"
                >
                  <MdAddCircleOutline className="w-4 h-4" />{" "}
                  {currentLang === "en" ? "Add Option" : "إضافة خيار"}
                </button>
              </div>

              {config.parent_id && !selectedParentConfig?.options?.length && (
                <div className="text-yellow-500 text-xs bg-yellow-500/10 p-2 rounded">
                  {currentLang === "en"
                    ? "Warning: The selected parent has no options yet. Add options to the parent first to link them."
                    : "تحذير: الحقل الأصلي المختار لا يحتوي على خيارات بعد. أضف خيارات للأصل أولاً لربطها."}
                </div>
              )}

              <div className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                {(filteredOptions || []).map((option, index) => (
                  <div
                    key={option.id}
                    className="flex flex-col gap-2 bg-black/20 p-3 rounded-lg border border-gray-700"
                  >
                    <div className="flex gap-3 py-2">
                      <input
                        type="text"
                        placeholder="EN Name"
                        required
                        value={option.en || ""}
                        onChange={(e) =>
                          updateOption(index, "en", e.target.value)
                        }
                        className="flex-1 px-1.5 py-2 bg-thirdly border border-gray-500/50 text-white rounded text-sm"
                      />
                      <input
                        type="text"
                        placeholder="AR Name"
                        required
                        value={option.ar || ""}
                        onChange={(e) =>
                          updateOption(index, "ar", e.target.value)
                        }
                        className="flex-1 px-1.5 py-2 bg-thirdly border border-gray-500/50 text-white rounded text-sm"
                      />

                      <div
                        className="w-20"
                        title={
                          currentLang === "en"
                            ? "Option Priority"
                            : "أولوية الخيار"
                        }
                      >
                        <input
                          type="number"
                          placeholder="Pri"
                          value={option.priority || 0}
                          onChange={(e) =>
                            updateOption(index, "priority", e.target.value)
                          }
                          className="w-full py-2 bg-thirdly border border-gray-500/50 text-white rounded text-center text-sm"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <MdOutlineDeleteOutline className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Relation Dropdown */}
                    {config.parent_id && selectedParentConfig && (
                      <div className="flex items-center gap-2 mt-1">
                        <FiLink className="text-gray-400 w-4 h-4" />
                        <select
                          required={!!config.parent_id}
                          value={option.relation_id || ""}
                          onChange={(e) =>
                            updateOption(index, "relation_id", e.target.value)
                          }
                          className="flex-1 px-2 py-2 bg-thirdly border border-gray-500/50 text-gray-300 rounded text-xs"
                        >
                          <option value="">
                            {currentLang === "en"
                              ? `Select Parent ${getInputLabel(config.parent_id)} Option`
                              : `اختر خيار ${getInputLabel(config.parent_id)} المرتبط`}
                          </option>

                          {selectedParentConfig.options &&
                            selectedParentConfig.options.map(
                              (parentOpt, pIdx) => (
                                // value={parentOpt.id} will be a positive integer now (1, 2, 3...)
                                <option key={pIdx} value={parentOpt.id}>
                                  {currentLang === "en"
                                    ? parentOpt.en
                                    : parentOpt.ar}
                                </option>
                              ),
                            )}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function AddCategoiresUi({
  categoriesData,
  inputsData,
  search,
}) {
  const nav = useNavigate();
  const [categoryId, setCategoryId] = useState(null);
  const [check, setCheck] = useState(true);
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const { i18n } = useTranslation();
  const t = i18n.t.bind(i18n);
  const currentLang = i18n.language;

  const [inputConfigurations, setInputConfigurations] = useState([]);

  // 2. Setup Positive ID Generator
  // Start at 0, first call becomes 1
  const tempIdCounter = useRef(0);
  const generateNewId = useCallback(() => {
    tempIdCounter.current = tempIdCounter.current + 1; // INCREMENT (Positive)
    return tempIdCounter.current;
  }, []);

  const handleChangeCheck = () => {
    setCheck(!check);
  };

  const onchange = (e) => {
    setCategoryId(e);
  };

  const toggleInputEnabled = (id, inputType) => {
    setInputConfigurations((prev) => {
      const exists = prev.some((item) => item.id === id);
      if (exists) {
        return prev.filter((item) => item.id !== id);
      } else {
        const needsOptions = [
          "select",
          "multi_select",
          "radio",
          "checkbox",
        ].includes(inputType);
        return [
          ...prev,
          {
            id: id,
            is_filtering: 0,
            type_filtering: null,
            priority: 0,
            parent_id: null,
            options: needsOptions ? [] : null,
          },
        ];
      }
    });
  };

  const updateInputConfig = (id, key, value) => {
    setInputConfigurations((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item)),
    );
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    const formData = new FormData(e.target);
    if (imageFile) {
      formData.append("logo", imageFile);
    }

    formData.set("active", check === true ? 1 : 0);
    if (categoryId) formData.append("category_id", categoryId);

    // inputConfigurations.forEach((config, key) => {
    //     formData.append(`inputs[${key}][id]`, config.id);
    //     formData.append(`inputs[${key}][priority]`, config.priority || 0);

    //     if(config.parent_id) {
    //         formData.append(`inputs[${key}][parent_id]`, config.parent_id);
    //     }

    //     const isFiltering = config.is_filtering === 1 ? 1 : 0;
    //     formData.append(`inputs[${key}][is_filtering]`, isFiltering);

    //     if (config.type_filtering) {
    //         formData.append(`inputs[${key}][type_filtering]`, config.type_filtering);
    //     }

    //     if (config.options && config.options.length > 0) {
    //         config.options.forEach((option, optionKey) => {
    //             if (option.en || option.ar) {

    //                 // Send the Positive ID
    //                 formData.append(`inputs[${key}][options][${optionKey}][id]`, option.id);

    //                 formData.append(`inputs[${key}][options][${optionKey}][en]`, option.en || '');
    //                 formData.append(`inputs[${key}][options][${optionKey}][ar]`, option.ar || '');
    //                 formData.append(`inputs[${key}][options][${optionKey}][priority]`, option.priority || 0);

    //                 if (option.relation_id) {
    //                     formData.append(`inputs[${key}][options][${optionKey}][relation_id]`, option.relation_id);
    //                 }
    //             }
    //         });
    //     }
    // });
    const cleanedInputs = inputConfigurations.map((config) => {
      // نأخذ نسخة من إعدادات الحقل الحالي
      let currentConfig = { ...config };

      // 1. تحويل parent_id إلى null إذا تم إرجاعه لحالة "بدون أصل"
      if (currentConfig.parent_id === "") {
        currentConfig.parent_id = null;
      }

      // 2. تنظيف الخيارات ومعالجة relation_id
      if (currentConfig.options && Array.isArray(currentConfig.options)) {
        currentConfig.options = currentConfig.options
          // حذف أي خيار فارغ أضافه المستخدم بالخطأ
          .filter((opt) => opt.en || opt.ar)
          // التعديل على الخيارات المتبقية
          .map((opt) => ({
            ...opt,
            // إذا كان relation_id نصاً فارغاً، نرسله كـ null للباك إند
            relation_id: opt.relation_id === "" ? null : opt.relation_id,
          }));
      }

      return currentConfig;
    });

    // إرسال المصفوفة بالكامل كمتغير واحد فقط!
    formData.append("inputs", JSON.stringify(cleanedInputs));
    const result = await addCategoiresForm(formData, navigate);
    setLoading(false);
  };

  const navigate = (route) => {
    nav(route);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(URL.createObjectURL(file));
      setImageFile(file);
    }
  };

  const handleDeleteImage = () => {
    setImage(null);
    setImageFile(null);
  };

  if (inputsData.length < 1) {
    return (
      <div className="flex justify-center items-center h-[25rem]">
        <ThreeDot
          variant="bounce"
          color="#D06421"
          size="large"
          text=""
          textColor=""
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="pb-10">
      {/* Basic Info Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="col-span-1">
          <label className="block text-white mb-2">
            {currentLang === "en" ? "Category" : "الفئة"}
          </label>
          <DropDownFilterPaginations
            search={search}
            options={categoriesData}
            value={categoryId}
            onchange={onchange}
            defaultText={
              currentLang === "en"
                ? "Select the category"
                : "أختر الفئة (اختياري) "
            }
            paramkey="category_id"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-white mb-2">
            {currentLang === "en" ? "Category Priority" : "أولوية الفئة"}
          </label>
          <input
            type="number"
            name="priority"
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            placeholder="0"
          />
        </div>

        <div className="">
          <label className="block text-white mb-2">
            {" "}
            {currentLang === "en" ? "Name (AR)" : "الاسم (عربي)"}{" "}
          </label>
          <input
            type="text"
            name="name_ar"
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            placeholder={
              currentLang === "en"
                ? "enter name in arabic"
                : "أدخل الاسم باللغة العربية "
            }
            required
          />
        </div>
        <div className="">
          <label className="block text-white mb-2">
            {currentLang === "en" ? "Name (EN)" : "الاسم (إنجليزي)"}
          </label>
          <input
            type="text"
            name="name_en"
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            placeholder={
              currentLang === "en"
                ? "enter name in english"
                : " أدخل الاسم باللغة الانجليزية "
            }
            required
          />
        </div>
        <div className="">
          <label className="block text-white mb-2">
            {" "}
            {currentLang === "en" ? "Description (AR)" : "الوصف (عربي)"}{" "}
          </label>
          <input
            type="text"
            name="description_ar"
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            placeholder={
              currentLang === "en"
                ? "enter description in arabic"
                : "أدخل الوصف باللغة العربية "
            }
          />
        </div>
        <div className="">
          <label className="block text-white mb-2">
            {currentLang === "en" ? "Description (EN)" : "الوصف (إنجليزي)"}{" "}
          </label>
          <input
            type="text"
            name="description_en"
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            placeholder={
              currentLang === "en"
                ? "enter description in english"
                : "أدخل الوصف باللغة الانجليزية "
            }
          />
        </div>
        <div className="flex gap-4 sm:flex-row flex-col sm:justify-between items-start mt-2">
          <div className="flex flex-row justify-center items-center gap-6">
            <div className="flex items-center gap-3">
              <div
                onClick={handleChangeCheck}
                className={`relative w-14 h-6 rounded-full cursor-pointer transition
                                ${check ? (currentLang === "ar" ? "bg-secondary" : "bg-secondary") : currentLang === "ar" ? "bg-gray-400" : "bg-gray-400"}`}
              >
                <span
                  className={`w-5 h-4 mt-1 bg-white rounded-full shadow-md block
                                    transform transition
                                    ${check ? (currentLang === "ar" ? "-translate-x-1" : "translate-x-7") : currentLang === "ar" ? "-translate-x-7" : "translate-x-1"}`}
                />
              </div>
              <label className="text-white">
                {check
                  ? currentLang === "en"
                    ? "active"
                    : "فعال"
                  : currentLang === "en"
                    ? "unactive"
                    : "غير فعال"}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Image Upload Section */}
      {!categoryId && (
        <div className="mb-6">
          <label className="block text-white mb-2">
            {currentLang === "en" ? "Photo" : "الصورة"}
          </label>
          <div className="bg-thirdly p-6 rounded-2xl border-2 border-dashed border-gray-500/50 hover:border-secondary transition-colors">
            <div className="relative h-40 flex justify-center items-center">
              {!image ? (
                <div className="text-center">
                  <FiUploadCloud className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm text-gray-300">
                    {currentLang === "en" ? "Click to upload" : "انقر للرفع"}
                  </span>
                  <input
                    type="file"
                    name="logo"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={handleImageChange}
                    required
                  />
                </div>
              ) : (
                <div className="relative h-full">
                  <img
                    src={image}
                    alt="Preview"
                    className="h-full object-contain rounded-lg"
                  />
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-lg"
                    onClick={handleDeleteImage}
                  >
                    <MdOutlineDeleteOutline className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- Advanced Input Configuration Section --- */}
      {categoryId && (
        <div className="mt-8 border-t border-gray-700 pt-6">
          <div className="flex items-center gap-2 mb-6">
            <MdLayers className="text-secondary w-6 h-6" />
            <h3 className="text-white text-xl font-semibold">
              {currentLang === "en"
                ? "Configure Inputs & Dependencies"
                : "تكوين الحقول والتبعيات"}{" "}
            </h3>
          </div>

          <div className="bg-primary/20 rounded-xl p-4 border border-gray-700">
            {inputsData.length > 0 ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {inputsData.map((item) => {
                  const config = inputConfigurations.find(
                    (c) => c.id === item.id,
                  );
                  const isEnabled = !!config;
                  const inputType = item.type || "text";

                  return (
                    <div
                      key={item.id}
                      className={`p-4 rounded-xl border transition-all duration-300 ${isEnabled ? "bg-gray-800/80 border-secondary/50" : "bg-transparent border-gray-700"}`}
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between gap-4 mb-2">
                        <div className="flex flex-col">
                          <span
                            className={`font-medium text-lg ${isEnabled ? "text-white" : "text-gray-400"}`}
                          >
                            {item.label?.[currentLang] || item.label?.en}
                          </span>
                          <span className="text-xs text-gray-500 uppercase tracking-wider">
                            {inputType}
                          </span>
                        </div>

                        <div
                          onClick={() => toggleInputEnabled(item.id, inputType)}
                          className={`relative w-12 h-6 rounded-full cursor-pointer transition-colors duration-200
                                                        ${isEnabled ? "bg-secondary" : "bg-gray-600"}`}
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200
                                                            ${
                                                              isEnabled
                                                                ? currentLang ===
                                                                  "ar"
                                                                  ? "left-1"
                                                                  : "left-7"
                                                                : currentLang ===
                                                                    "ar"
                                                                  ? "left-7"
                                                                  : "left-1"
                                                            }`}
                          />
                        </div>
                      </div>

                      {/* Configurator */}
                      {isEnabled && (
                        <InputConfigurator
                          input={item}
                          config={config}
                          updateConfig={updateInputConfig}
                          currentLang={currentLang}
                          allConfigs={inputConfigurations}
                          inputsData={inputsData}
                          generateNewId={generateNewId}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-white text-center py-10">
                {currentLang === "en"
                  ? "No inputs available."
                  : "لا توجد حقول متاحة."}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-center w-full mt-8">
        <button
          type="submit"
          disabled={loading}
          className={`w-full md:w-1/3 bg-secondary text-white py-3 rounded-xl font-semibold shadow-lg transition hover:bg-orange-600 ${loading ? "cursor-not-allowed opacity-70" : ""}`}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
              {currentLang === "en" ? "Saving..." : "جاري الحفظ..."}
            </div>
          ) : (
            t("button.save")
          )}
        </button>
      </div>
    </form>
  );
}
